/**
 * Atendimento — central de conversas em tempo real (migration 024).
 *
 * Inbox de 3 colunas: lista de conversas · área do chat · contexto do cliente.
 * As conversas referenciam contacts/companies/deals do CRM (não duplicam). O
 * Realtime é a via primária de atualização; o polling só reconcilia o que ele
 * perder (anexos, reconexão).
 *
 * Layout v2 — fiel ao conceito titam-crm-atendimento-conceito-v1.png.
 */

import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  atListConversations, atGetConversation, atCreateConversation,
  atUpdateConversation, atDeleteConversation, atAssignConversation,
  atMarkRead, atMarkUnread, atListMessages, atSendMessage,
  atListPresence, atSetPresence,
  atSignUpload, atCreateAttachment, atDownloadAttachment, atUploadToSignedUrl,
  getCrmContact, getCrmContactDeals, getCrmContactActivities, getCrmContactHistory,
  createCrmContactActivity, updateActivity, getCrmMembers, updateCrmContact,
} from '../../db/api.js'
import {
  dmWindowExpiry, formatWindowLeft, isDmConversation, isDmWindowOpen,
} from '../../lib/dmWindow.js'
import { isMetaWhatsAppConversation } from '../../lib/waTemplate.js'
import WhatsappTemplatePicker from './WhatsappTemplatePicker.jsx'
import {
  FILTERS, STATUS_LABEL, ACT_TIPO, CHANNEL_META, avatarColor, initials,
  displayName, relTime, msgTime, groupByDay, money, prazoLabel,
  QUICK_REPLIES, actIcon,
} from './atendimentoKit.jsx'
import NewConversationModal from './NewConversationModal.jsx'
import DealModal from '../comercial/DealModal.jsx'
import DealPanel from '../comercial/DealPanel.jsx'
import DealCompany from '../comercial/DealCompany.jsx'
import { loadContactContext, selectContextDeal } from './contactContext.js'
import { findMessageInHistory, mergeMessages } from './messageNavigation.js'
import VoiceMessage from './VoiceMessage.jsx'
import ContactHistory from './ContactHistory.jsx'
import { insertEmojiAtSelection } from './emojiInsertion.js'
import {
  ensureRealtimeAuth, subscribeConversations, subscribeMessages, subscribeTyping,
} from './realtime.js'
import AdOriginCard from '../../components/AdOriginCard.jsx'
import './atendimento.css'

// Realtime é a via primária; o polling vira só reconciliador de fundo (pega o
// que o Realtime perder: anexos, reconexão) em intervalo folgado.
const LIST_POLL_MS = 20000
const MSG_POLL_MS = 20000
const ChatEmojiPicker = lazy(() => import('./ChatEmojiPicker.jsx'))

export default function Atendimento() {
  const { selectedClient, user, toast, confirm } = useApp()
  const clientId = selectedClient?.id || null
  const userId = user?.id || null

  const [convs, setConvs] = useState([])
  const [loadingList, setLoadingList] = useState(true)
  const [listErr, setListErr] = useState(null)
  const [filtro, setFiltro] = useState('todas')
  const [busca, setBusca] = useState('')
  const [activeId, setActiveId] = useState(null)
  const [historyConversation, setHistoryConversation] = useState(null)
  const [messageTarget, setMessageTarget] = useState(null)
  const navigationRef = useRef(0)
  useEffect(() => {
    navigationRef.current++
    setHistoryConversation(null); setMessageTarget(null)
    return () => { navigationRef.current++ }
  }, [clientId])
  const [showNew, setShowNew] = useState(false)
  const [members, setMembers] = useState([])

  // operadores atribuíveis (assumir/transferir) — mesma fonte do CRM
  useEffect(() => {
    if (!clientId) { setMembers([]); return }
    getCrmMembers().then((rows) => setMembers(Array.isArray(rows) ? rows : [])).catch(() => {})
  }, [clientId])

  // ---- lista de conversas ------------------------------------------------
  const loadList = useCallback(async () => {
    if (!clientId) return
    try {
      const rows = await atListConversations({ filtro, busca })
      setConvs(rows)
      setListErr(null)
    } catch (e) { setListErr(e.message) }
    finally { setLoadingList(false) }
  }, [clientId, filtro, busca])

  // reconciliador de fundo (poll folgado) — segue os filtros/busca atuais
  useEffect(() => {
    setConvs([]); setLoadingList(true)
    if (!clientId) { setLoadingList(false); return }
    loadList()
    const t = setInterval(loadList, LIST_POLL_MS)
    return () => clearInterval(t)
  }, [clientId, loadList])

  // Realtime das conversas do client (nova/preview/status/unread). Keyed só no
  // clientId; usa um ref p/ chamar o loadList mais recente sem re-assinar a cada
  // troca de filtro. Debounce curto p/ agrupar rajadas.
  const loadListRef = useRef(loadList)
  loadListRef.current = loadList
  useEffect(() => {
    if (!clientId) return
    let timer = null
    let unsub = () => {}
    ;(async () => {
      await ensureRealtimeAuth()
      unsub = subscribeConversations(clientId, () => {
        clearTimeout(timer)
        timer = setTimeout(() => loadListRef.current?.(), 250)
      })
    })()
    return () => { clearTimeout(timer); unsub() }
  }, [clientId])

  const active = convs.find((c) => c.id === activeId) || (historyConversation?.id === activeId ? historyConversation : null)
  const handleContactUpdated = (id, patch) => {
    setConvs(rows => rows.map(row => row.contact_id === id ? { ...row, ...patch } : row))
    setHistoryConversation(current => current?.contact_id === id ? { ...current, ...patch } : current)
  }

  const openConversation = async (id) => {
    navigationRef.current++
    setMessageTarget(null); setHistoryConversation(null)
    setActiveId(id)
    try { await atMarkRead(id) } catch { /* silencioso */ }
    // zera o badge local imediatamente
    setConvs((cs) => cs.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c)))
  }

  const openHistoryConversation = async (conversation, messageId) => {
    const request = ++navigationRef.current
    try {
      const row = convs.find(c => c.id === conversation.id) || await atGetConversation(conversation.id)
      if (request !== navigationRef.current) return
      setHistoryConversation(row)
      setActiveId(row.id)
      setMessageTarget(messageId ? { conversationId: row.id, messageId, request } : null)
      try { await atMarkRead(row.id) } catch { /* silencioso */ }
    } catch (e) { if (request === navigationRef.current) toast?.(e.message, 'error') }
  }

  // Deep-link ?conversation=<id> (ex.: botão "Ir para o atendimento" da aba
  // Atendimentos do Deal/Lead). Abre assim que a conversa aparecer na lista
  // carregada; roda só uma vez por navegação.
  const [searchParams] = useSearchParams()
  const deepLinkedRef = useRef(false)
  useEffect(() => {
    if (deepLinkedRef.current) return
    const wanted = searchParams.get('conversation')
    if (!wanted || !convs.length) return
    if (convs.some((c) => c.id === wanted)) {
      deepLinkedRef.current = true
      openConversation(wanted)
    }
  }, [searchParams, convs]) // eslint-disable-line react-hooks/exhaustive-deps

  const patchConv = (id, patch) =>
    setConvs((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)))

  // ---- ações de conversa -------------------------------------------------
  const toggleFavorite = async (conv) => {
    const next = !conv.is_favorite
    patchConv(conv.id, { is_favorite: next })
    try { await atUpdateConversation(conv.id, { is_favorite: next }) }
    catch (e) { toast?.(e.message, 'error'); patchConv(conv.id, { is_favorite: conv.is_favorite }) }
  }

  const setStatus = async (conv, status) => {
    try {
      await atUpdateConversation(conv.id, { status })
      toast?.(`Conversa ${STATUS_LABEL[status].toLowerCase()}.`, 'success')
      if (status === 'arquivada' && activeId === conv.id) setActiveId(null)
      loadList()
    } catch (e) { toast?.(e.message, 'error') }
  }

  const assumir = async (conv) => {
    try {
      const row = await atAssignConversation(conv.id, userId)
      patchConv(conv.id, { assigned_to: row.assigned_to })
      toast?.('Você assumiu o atendimento.', 'success')
    } catch (e) { toast?.(e.message, 'error') }
  }

  const transferir = async (conv, toUserId) => {
    try {
      const row = await atAssignConversation(conv.id, toUserId)
      patchConv(conv.id, { assigned_to: row.assigned_to })
      const nome = members.find((m) => m.id === toUserId)?.nome
      toast?.(toUserId
        ? `Atendimento transferido${nome ? ` para ${nome}` : ''}.`
        : 'Atendimento liberado.', 'success')
    } catch (e) { toast?.(e.message, 'error') }
  }

  const marcarNaoLida = async (conv) => {
    try { await atMarkUnread(conv.id); patchConv(conv.id, { unread_count: 1 }); toast?.('Marcada como não lida.') }
    catch (e) { toast?.(e.message, 'error') }
  }

  const removeConv = async (conv) => {
    if (!(await confirm?.('Excluir esta conversa? O histórico será ocultado.'))) return
    try {
      await atDeleteConversation(conv.id)
      if (activeId === conv.id) setActiveId(null)
      loadList()
    } catch (e) { toast?.(e.message, 'error') }
  }

  // ---- nova conversa -----------------------------------------------------
  const startConversation = async (contato, opts = {}) => {
    setShowNew(false)
    try {
      const conv = await atCreateConversation({
        contact_id: contato.id,
        company_id: contato.company_id || undefined,
        ...opts,   // { channel, wa_provider, wa_instance_name, wa_remote_jid }
      })
      await loadList()
      setActiveId(conv.id)
    } catch (e) { toast?.(e.message, 'error') }
  }

  const topbarRight = <TopbarTools clientId={clientId} userId={userId} toast={toast} />

  if (!clientId) {
    return (
      <Layout title="Atendimento" breadcrumb={[{ label: 'Atendimento' }]} topbarRight={topbarRight}>
        <div className="at-empty at-empty-full">
          <Icon n="attend" s={40} />
          <p>Selecione um cliente para ver as conversas.</p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout title="Atendimento" breadcrumb={[{ label: 'Atendimento' }]} topbarRight={topbarRight}>
      <div className={`at-inbox${active ? ' has-active' : ''}`}>
        <ConversationListPane
          convs={convs}
          loading={loadingList}
          err={listErr}
          filtro={filtro}
          setFiltro={setFiltro}
          busca={busca}
          setBusca={setBusca}
          activeId={activeId}
          onOpen={openConversation}
          onNew={() => setShowNew(true)}
        />

        {active ? (
          <ChatPane
            key={active.id}
            conv={active}
            onContactUpdated={handleContactUpdated}
            messageTarget={messageTarget?.conversationId === active.id ? messageTarget : null}
            userId={userId}
            members={members}
            onFavorite={() => toggleFavorite(active)}
            onArchive={() => setStatus(active, 'arquivada')}
            onClose={() => setStatus(active, active.status === 'encerrada' ? 'aberta' : 'encerrada')}
            onAssumir={() => assumir(active)}
            onTransfer={(uid) => transferir(active, uid)}
            onUnread={() => marcarNaoLida(active)}
            onDelete={() => removeConv(active)}
            onSent={loadList}
            onBack={() => setActiveId(null)}
          />
        ) : (
          <div className="at-chat at-empty">
            <Icon n="attend" s={40} />
            <p>Selecione uma conversa para começar.</p>
          </div>
        )}

        {active && <ContactPane key={`${clientId}:${active.id}:${active.contact_id}`} conv={active} onOpenHistoryConversation={openHistoryConversation} onContactUpdated={handleContactUpdated} />}
      </div>

      {showNew && (
        <NewConversationModal onClose={() => setShowNew(false)} onPick={startConversation} />
      )}
    </Layout>
  )
}

/* ========================================================================= */
/* Topbar da página — status do operador · notificações · ajuda              */
/* ========================================================================= */
const PRESENCE = [
  { id: 'disponivel', label: 'Disponível', cls: '' },
  { id: 'ausente', label: 'Ausente', cls: 'is-away' },
  { id: 'offline', label: 'Offline', cls: 'is-off' },
]

function TopbarTools({ clientId = null, userId = null, toast = null }) {
  const [presence, setPresence] = useState(PRESENCE[0])
  const [open, setOpen] = useState(false)

  // A presença fica salva no backend (as automações a leem); sem linha = disponível.
  useEffect(() => {
    if (!clientId || !userId) return undefined
    let alive = true
    atListPresence()
      .then((rows) => {
        const mine = (Array.isArray(rows) ? rows : []).find((r) => r.user_id === userId)
        if (alive) setPresence(PRESENCE.find((p) => p.id === mine?.status) || PRESENCE[0])
      })
      .catch(() => {})
    return () => { alive = false }
  }, [clientId, userId])

  const choose = async (p) => {
    const previous = presence
    setPresence(p)
    setOpen(false)
    try { await atSetPresence(p.id) }
    catch (e) { setPresence(previous); toast?.(e.message || 'Não foi possível salvar o status.', 'error') }
  }

  return (
    <div className="at-tb">
      <div className="at-menu-wrap">
        <button
          className={`at-tb-status ${presence.cls}`}
          onClick={() => setOpen((v) => !v)}
          title="Status de atendimento"
        >
          <span className="at-tb-dot" />
          {presence.label}
          <Icon n="chevron_d" s={14} c="var(--ter)" />
        </button>
        {open && (
          <>
            <div className="at-menu-backdrop" onClick={() => setOpen(false)} />
            <div className="at-menu">
              {PRESENCE.map((p) => (
                <button key={p.id} onClick={() => choose(p)}>
                  {p.label}{p.id === presence.id ? ' ✓' : ''}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/* ========================================================================= */
/* Coluna 1 — lista de conversas                                             */
/* ========================================================================= */
function ConversationListPane({
  convs, loading, err, filtro, setFiltro, busca, setBusca, activeId, onOpen, onNew,
}) {
  // Um único botão quadrado ao lado da busca (como no conceito): abre o menu
  // com "Nova conversa" + os mesmos filtros das abas.
  const [tools, setTools] = useState(false)

  return (
    <aside className="at-list">
      <div className="at-list-head">
        <div className="at-search">
          <Icon n="search" s={16} />
          <input
            placeholder="Buscar conversas"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="at-menu-wrap">
          <button
            className="at-new-btn"
            onClick={() => setTools((v) => !v)}
            title="Nova conversa e filtros"
          >
            <Icon n="sliders" s={17} />
          </button>
          {tools && (
            <>
              <div className="at-menu-backdrop" onClick={() => setTools(false)} />
              <div className="at-menu">
                <button onClick={() => { setTools(false); onNew() }}>＋ Nova conversa</button>
                <div className="at-menu-title">Filtrar</div>
                {FILTERS.map((f) => (
                  <button key={f.id} onClick={() => { setTools(false); setFiltro(f.id) }}>
                    {f.label}{filtro === f.id ? ' ✓' : ''}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="at-tabs">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            className={`at-tab${filtro === f.id ? ' is-active' : ''}`}
            onClick={() => setFiltro(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="at-list-body">
        {loading && <ListSkeleton />}
        {err && <div className="at-error at-pad">{err}</div>}
        {!loading && !err && convs.length === 0 && (
          <div className="at-empty at-empty-list">
            <Icon n="attend" s={30} />
            <p>Nenhuma conversa aqui.</p>
          </div>
        )}
        {convs.map((c) => (
          <ConversationRow
            key={c.id}
            conv={c}
            active={c.id === activeId}
            onClick={() => onOpen(c.id)}
          />
        ))}
      </div>
    </aside>
  )
}

function ConversationRow({ conv, active, onClick }) {
  const nome = displayName(conv)
  const unread = Number(conv.unread_count || 0)
  const ch = CHANNEL_META[conv.channel]
  return (
    <button className={`at-conv${active ? ' is-active' : ''}`} onClick={onClick}>
      <ContactAvatar conv={conv} nome={nome} channelDot />
      <span className="at-conv-body">
        <span className="at-conv-top">
          <span className={`at-conv-name${unread ? ' is-unread' : ''}`}>{nome}</span>
          <span className="at-conv-time">{relTime(conv.last_message_at)}</span>
        </span>
        <span className="at-conv-bottom">
          <span className="at-conv-preview">
            {conv.channel !== 'whatsapp' && ch && <span className={`at-ch-tag at-ch-${conv.channel}`}>{ch.label}</span>}
            {conv.last_message_preview || 'Sem mensagens'}
          </span>
          <span className="at-conv-marks">
            {conv.is_favorite && <Icon n="star" s={12} />}
            {unread > 0 && <span className="at-badge">{unread > 99 ? '99+' : unread}</span>}
          </span>
        </span>
      </span>
    </button>
  )
}

function ContactAvatar({ conv, nome, size = '', channelDot = false, presence = false }) {
  const ch = CHANNEL_META[conv.channel]
  const [badPic, setBadPic] = useState(false)
  useEffect(() => { setBadPic(false) }, [conv.contact_pic])
  const pic = badPic ? null : conv.contact_pic
  return (
    <span className={`at-avatar${size ? ` ${size}` : ''}${pic ? ' has-img' : ''}`} style={{ background: avatarColor(nome) }}>
      {pic ? <img src={pic} alt="" onError={() => setBadPic(true)} /> : initials(nome)}
      {presence && <span className="at-presence" />}
      {channelDot && ch && (
        <span className={`at-ch-dot at-ch-${conv.channel}`} title={ch.label}>
          <Icon n={ch.icon} s={9} />
        </span>
      )}
    </span>
  )
}

function ListSkeleton() {
  return (
    <div className="at-skel-list">
      {Array.from({ length: 8 }).map((_, i) => (
        <div className="at-skel-row" key={i}>
          <div className="at-skel-avatar" />
          <div className="at-skel-lines">
            <div className="at-skel-line w60" />
            <div className="at-skel-line w90" />
          </div>
        </div>
      ))}
    </div>
  )
}

/* ========================================================================= */
/* Coluna 2 — área do chat                                                   */
/* ========================================================================= */
function ChatContactName({ contactId, name, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const locked = useRef(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  const save = async event => {
    event.preventDefault()
    if (!contactId || locked.current) return
    const next = draft.trim()
    if (!next) { setError('Informe o nome do contato.'); return }
    if (next === name) { setEditing(false); return }
    locked.current = true
    setSaving(true); setError('')
    try {
      await updateCrmContact(contactId, { nome: next })
      if (!mounted.current) return
      onSaved(contactId, { contact_nome: next })
      setEditing(false)
    } catch (err) {
      if (mounted.current) setError(err.message || 'Não foi possível salvar o nome.')
    } finally {
      locked.current = false
      if (mounted.current) setSaving(false)
    }
  }
  return <span className="at-chat-name-field">
    <span className="at-chat-name at-chat-name-measure" aria-hidden="true">{name}</span>
    {!editing ? <button type="button" className="at-chat-name at-chat-name-trigger" disabled={!contactId} title="Editar nome do contato" onClick={() => { setDraft(name); setError(''); setEditing(true) }}>{name}</button>
      : <form className="at-chat-name-editor" onSubmit={save} onKeyDown={event => {
        if (event.key === 'Escape') { event.stopPropagation(); if (!locked.current) setEditing(false) }
      }}>
        <input autoFocus aria-label="Nome do contato na conversa" aria-invalid={!!error} aria-describedby={error ? 'at-chat-name-error' : undefined} maxLength={200} value={draft} disabled={saving} onChange={event => setDraft(event.target.value)} />
        <button type="button" disabled={saving} title="Cancelar" aria-label="Cancelar edição do nome" onClick={() => setEditing(false)}><Icon n="x" s={14} /></button>
        <button type="submit" disabled={saving} title={saving ? 'Salvando…' : 'Confirmar'} aria-label={saving ? 'Salvando nome' : 'Confirmar nome'}><Icon n={saving ? 'clock' : 'check'} s={15} /></button>
        {error && <small id="at-chat-name-error" role="alert">{error}</small>}
      </form>}
  </span>
}

function ChatPane({
  conv, userId, members = [], onFavorite, onArchive, onClose, onAssumir, onTransfer,
  onUnread, onDelete, onSent, onBack, messageTarget, onContactUpdated,
}) {
  const { toast, user, avatarUrl } = useApp()
  const [messages, setMessages] = useState([])
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  const [locatingMessage, setLocatingMessage] = useState(false)
  const [readyTarget, setReadyTarget] = useState(null)
  const [highlightedMessage, setHighlightedMessage] = useState(null)
  const centeredRequest = useRef(null)
  const messageNodes = useRef(new Map())
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [files, setFiles] = useState([])
  const [menu, setMenu] = useState(false)
  const [ownerMenu, setOwnerMenu] = useState(false)
  const [pop, setPop] = useState(null)   // null | 'emoji' | 'quick'
  const [othersTyping, setOthersTyping] = useState(false)
  const scrollRef = useRef(null)
  const bottomRef = useRef(null)
  const textRef = useRef(null)
  const emojiSelection = useRef({ start: 0, end: 0 })
  const fileRef = useRef(null)
  const stickToBottom = useRef(true)
  const notifyTypingRef = useRef(null)
  const typingClearRef = useRef(null)

  // Instagram Direct e Messenger (#344): só dá para responder nas 24 horas seguintes à última
  // mensagem do contato, e só com texto. O servidor recusa fora da janela (409); aqui avisamos antes.
  const isDm = isDmConversation(conv)
  const [dmServerExpiry, setDmServerExpiry] = useState(null)
  const [clock, setClock] = useState(() => Date.now())
  // WhatsApp Oficial (#28): a mesma janela de 24 horas, mas fora dela o operador ainda fala com
  // o contato por template aprovado (seletor abaixo), em vez de ficar travado.
  const isMetaWa = isMetaWhatsAppConversation(conv)
  const [templatePicker, setTemplatePicker] = useState(false)
  const windowed = isDm || isMetaWa
  const dmExpiry = windowed ? dmWindowExpiry(messages, dmServerExpiry) : null
  const windowClosed = windowed && !isDmWindowOpen(dmExpiry, clock)
  const dmClosed = isDm && windowClosed
  const waClosed = isMetaWa && windowClosed

  useEffect(() => {
    if (!windowed) return undefined
    let alive = true
    atGetConversation(conv.id)
      .then((c) => {
        if (alive) setDmServerExpiry(c?.dm_window_expires_at || c?.wa_window_expires_at || null)
      })
      .catch(() => {})
    const t = setInterval(() => setClock(Date.now()), 60000)
    return () => { alive = false; clearInterval(t) }
  }, [windowed, conv.id])

  const nome = displayName(conv)
  const mine = conv.assigned_to && conv.assigned_to === userId
  const ownerName = mine
    ? 'Você'
    : (members.find((m) => m.id === conv.assigned_to)?.nome || 'Não atribuído')
  const ch = CHANNEL_META[conv.channel]

  const load = useCallback(async () => {
    try {
      const rows = await atListMessages(conv.id, { limit: 50 })
      setMessages(current => mergeMessages(current, rows))
    } catch { /* mantém o que tem */ }
    finally { setLoading(false) }
  }, [conv.id])

  useEffect(() => {
    setLoading(true); setMessages([])
    load()
    const t = setInterval(load, MSG_POLL_MS)
    return () => clearInterval(t)
  }, [load])

  // Realtime da conversa: nova mensagem (INSERT) e recibo/edição (UPDATE).
  // Dedup por id — a própria mensagem enviada já entrou otimista + via POST.
  useEffect(() => {
    let unsubMsgs = () => {}
    let typing = null
    ;(async () => {
      await ensureRealtimeAuth()
      unsubMsgs = subscribeMessages(conv.id, {
        onInsert: (row) => {
          setMessages((m) => (m.some((x) => x.id === row.id)
            ? m
            : [...m, { ...row, attachments: row.attachments || [] }]))
        },
        onUpdate: (row) => setMessages((m) => m.map((x) => (x.id === row.id ? { ...x, ...row } : x))),
      })
      typing = subscribeTyping(conv.id, userId, () => {
        setOthersTyping(true)
        clearTimeout(typingClearRef.current)
        typingClearRef.current = setTimeout(() => setOthersTyping(false), 3000)
      })
      notifyTypingRef.current = typing.notifyTyping
    })()
    return () => {
      unsubMsgs(); typing?.unsubscribe?.()
      notifyTypingRef.current = null
      clearTimeout(typingClearRef.current)
      setOthersTyping(false)
    }
  }, [conv.id, userId])

  useEffect(() => {
    if (!messageTarget) {
      setLocatingMessage(false); setReadyTarget(null); setHighlightedMessage(null)
      return
    }
    let cancelled = false
    stickToBottom.current = false
    setLocatingMessage(true); setReadyTarget(null)
    findMessageInHistory(options => atListMessages(conv.id, options), messageTarget.messageId, messagesRef.current, () => cancelled)
      .then(rows => {
        if (cancelled) return
        if (!rows) { toast?.('Esta mensagem não está mais disponível nesta conversa.', 'error'); return }
        setMessages(current => mergeMessages(current, rows))
        setReadyTarget(messageTarget)
      })
      .catch(error => { if (!cancelled) toast?.(error.message, 'error') })
      .finally(() => { if (!cancelled) setLocatingMessage(false) })
    return () => { cancelled = true }
  }, [conv.id, messageTarget, toast])

  useLayoutEffect(() => {
    if (!readyTarget || loading || locatingMessage || centeredRequest.current === readyTarget.request) return
    const container = scrollRef.current
    const node = messageNodes.current.get(readyTarget.messageId)
    if (!container || !node) return
    centeredRequest.current = readyTarget.request
    const bounds = container.getBoundingClientRect(), messageBounds = node.getBoundingClientRect()
    container.scrollTop += messageBounds.top - bounds.top - (container.clientHeight - messageBounds.height) / 2
    stickToBottom.current = false
    setHighlightedMessage(readyTarget.messageId)
    const timeout = setTimeout(() => setHighlightedMessage(null), 4000)
    return () => clearTimeout(timeout)
  }, [readyTarget, loading, locatingMessage])

  // auto-scroll ao chegar mensagem, se o usuário está no fim. Rola só o
  // container da timeline (scrollTop) — scrollIntoView rolaria também os
  // ancestrais (.page), deslocando cabeçalho/campo de envio.
  useEffect(() => {
    if (!stickToBottom.current) return
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages])

  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  // textarea cresce com o conteúdo até o teto do CSS (max-height)
  const autoGrow = (el) => {
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`
  }

  const insert = (snippet) => {
    setText((t) => (t ? `${t}${t.endsWith(' ') ? '' : ' '}${snippet}` : snippet))
    setPop(null)
    requestAnimationFrame(() => { textRef.current?.focus(); autoGrow(textRef.current) })
  }

  const insertEmoji = emoji => {
    const result = insertEmojiAtSelection(text, emoji, emojiSelection.current.start, emojiSelection.current.end)
    setText(result.text)
    emojiSelection.current = { start: result.cursor, end: result.cursor }
    requestAnimationFrame(() => {
      textRef.current?.setSelectionRange(result.cursor, result.cursor)
      autoGrow(textRef.current)
    })
  }
  const closeEmojis = () => { setPop(null); textRef.current?.focus() }

  const send = async () => {
    const body = text.trim()
    if ((!body && files.length === 0) || sending || windowClosed) return
    const picked = files
    setSending(true)
    setText('')
    setFiles([])
    if (textRef.current) textRef.current.style.height = 'auto'
    const tempId = `tmp-${Date.now()}`
    const optimistic = {
      id: tempId, sender_type: 'operador', sender_user_id: userId, body,
      status: 'enviando', created_at: new Date().toISOString(),
      attachments: picked.map((file, i) => ({
        id: `${tempId}-${i}`, nome: file.name, mime: file.type, tamanho: file.size, local: true,
      })),
    }
    setMessages((m) => [...m, optimistic])
    stickToBottom.current = true
    try {
      const saved = await atSendMessage(conv.id, {
        body: body || null,
        client_msg_id: tempId,
        defer_channel_send: picked.length > 0,
      })
      const savedAttachments = []
      for (const file of picked) {
        const signed = await atSignUpload(saved.id, file.name)
        await atUploadToSignedUrl(signed.upload_url, file)
        const att = await atCreateAttachment(saved.id, {
          nome: file.name,
          storage_path: signed.storage_path,
          mime: file.type || 'application/octet-stream',
          tamanho: file.size,
        })
        savedAttachments.push(att)
      }
      // troca o otimista pelo salvo e remove eventual duplicata já injetada pelo
      // Realtime (INSERT do mesmo id pode chegar antes desta resposta).
      setMessages((m) => [
        ...m.filter((x) => x.id !== tempId && x.id !== saved.id),
        { ...saved, attachments: savedAttachments.length ? savedAttachments : (saved.attachments || []) },
      ])
      if (picked.length) await load()
      onSent?.()
    } catch (e) {
      setMessages((m) => m.map((x) => (x.id === tempId ? { ...x, status: 'falhou' } : x)))
      setFiles(picked)
      toast?.(e.message || 'Falha ao enviar mensagem.', 'error')
    } finally { setSending(false) }
  }

  // Template aprovado (WhatsApp Oficial, janela de 24h fechada). Sem otimismo: o servidor valida
  // a conta e a aprovação, então só mostramos a mensagem depois que ele grava.
  const sendTemplate = async (waTemplate, preview) => {
    try {
      const saved = await atSendMessage(conv.id, {
        body: preview, wa_template: waTemplate, client_msg_id: `tmp-${Date.now()}`,
      })
      setMessages((m) => [...m.filter((x) => x.id !== saved.id), { ...saved, attachments: saved.attachments || [] }])
      stickToBottom.current = true
      onSent?.()
    } catch (e) {
      toast?.(e.message || 'Falha ao enviar o template.', 'error')
      throw e
    }
  }

  const pickFiles = (event) => {
    const incoming = Array.from(event.target.files || [])
    if (!incoming.length) return
    if (conv.channel === 'whatsapp') {
      setFiles([incoming[0]])
      if (incoming.length > 1) toast?.('No WhatsApp, envie um arquivo por mensagem.', 'info')
    } else {
      setFiles((current) => [...current, ...incoming].slice(0, 8))
    }
    event.target.value = ''
  }

  const removeFile = (index) => {
    setFiles((current) => current.filter((_, i) => i !== index))
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
  }

  const groups = groupByDay(messages)

  return (
    <section className="at-chat">
      {/* cabeçalho */}
      <header className="at-chat-head">
        <button className="at-back-btn" onClick={onBack} aria-label="Voltar para a lista">
          <Icon n="chevron_l" s={20} />
        </button>

        <ContactAvatar conv={conv} nome={nome} size="at-avatar-md" presence />

        <div className="at-chat-head-info">
          <div className="at-chat-name-row">
            <ChatContactName key={conv.contact_id} contactId={conv.contact_id} name={nome} onSaved={onContactUpdated} />
            {conv.status !== 'aberta' && (
              <span className={`at-chip at-chip-${conv.status}`}>{STATUS_LABEL[conv.status]}</span>
            )}
          </div>

          <div className="at-chat-sub">
            {othersTyping ? (
              <span className="at-typing">digitando<span className="at-typing-dots">…</span></span>
            ) : (
              <div className="at-owner-wrap">
                <button className="at-owner-btn" onClick={() => setOwnerMenu((v) => !v)}>
                  Proprietário: <b>{ownerName}</b>
                  <Icon n="chevron_d" s={13} />
                </button>
                {ownerMenu && (
                  <>
                    <div className="at-menu-backdrop" onClick={() => setOwnerMenu(false)} />
                    <div className="at-menu at-menu-left at-menu-scroll">
                      <div className="at-menu-title">Atribuir a</div>
                      <button onClick={() => { setOwnerMenu(false); onAssumir() }}>
                        Eu mesmo{mine ? ' ✓' : ''}
                      </button>
                      {members.filter((m) => m.id !== userId).map((m) => (
                        <button
                          key={m.id}
                          onClick={() => { setOwnerMenu(false); onTransfer(m.id) }}
                        >
                          {m.nome || 'Operador'}{m.id === conv.assigned_to ? ' ✓' : ''}
                        </button>
                      ))}
                      {conv.assigned_to && (
                        <button onClick={() => { setOwnerMenu(false); onTransfer(null) }}>
                          Não atribuído
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {ch && (
              <span className={`at-ch-chip at-ch-${conv.channel}`}>
                <Icon n={ch.icon} s={12} />
                {ch.label}
              </span>
            )}
          </div>
        </div>

        <div className="at-chat-actions">
          {mine ? (
            <span className="at-you-chip"><Icon n="check" s={13} /> Você atende</span>
          ) : (
            <button className="at-btn-assume" onClick={onAssumir}>Assumir atendimento</button>
          )}

          <div className="at-menu-wrap">
            <button className="at-icon-btn" onClick={() => setMenu((v) => !v)} title="Mais ações">
              <Icon n="more" s={18} />
            </button>
            {menu && (
              <>
                <div className="at-menu-backdrop" onClick={() => setMenu(false)} />
                <div className="at-menu">
                  <button onClick={() => { setMenu(false); onFavorite() }}>
                    {conv.is_favorite ? 'Remover dos favoritos' : 'Favoritar'}
                  </button>
                  <button onClick={() => { setMenu(false); onUnread() }}>Marcar não lida</button>
                  <button onClick={() => { setMenu(false); onClose() }}>
                    {conv.status === 'encerrada' ? 'Reabrir' : 'Encerrar'}
                  </button>
                  <button onClick={() => { setMenu(false); onArchive() }}>Arquivar</button>
                  <button className="at-menu-danger" onClick={() => { setMenu(false); onDelete() }}>
                    Excluir
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* timeline */}
      <div className="at-messages" ref={scrollRef} onScroll={onScroll}>
        {locatingMessage && <div role="status" className="at-message-locating">Localizando mensagem…</div>}
        {loading && <MessagesSkeleton />}
        {!loading && messages.length === 0 && (
          <div className="at-empty at-empty-msgs">
            <Icon n="send" s={28} />
            <p>Nenhuma mensagem ainda. Diga olá 👋</p>
          </div>
        )}
        {groups.map((g) => (
          <div key={g.key}>
            <div className="at-date-sep"><span>{g.sep}</span></div>
            {g.items.map((m) => {
              const outgoing = m.sender_type === 'operador'
              const sender = outgoing ? members.find(member => member.id === m.sender_user_id) : null
              const fromCurrentUser = outgoing && m.sender_user_id === user?.id
              const senderPhoto = outgoing ? sender?.avatar_url || (fromCurrentUser ? avatarUrl : null) : conv.contact_pic
              const senderName = outgoing ? sender?.nome || (fromCurrentUser ? user.name : null) : nome
              return <div key={m.id} data-message-id={m.id} className={highlightedMessage === m.id ? 'at-message-highlight' : undefined} ref={node => { if (node) messageNodes.current.set(m.id, node); else messageNodes.current.delete(m.id) }}><MessageBubble msg={m} voiceStyle avatar={senderPhoto} senderName={senderName} /></div>
            })}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* composer */}
      <div className="at-composer">
        {files.length > 0 && (
          <div className="at-pending-files">
            {files.map((file, i) => (
              <span key={`${file.name}-${i}`} className="at-pending-file">
                <Icon n={file.type?.startsWith('audio/') ? 'attend' : 'clip'} s={13} />
                <span>{file.name}</span>
                <button onClick={() => removeFile(i)} aria-label="Remover arquivo">
                  <Icon n="close" s={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        {isMetaWa && (
          <div className={`at-dm-window${waClosed ? ' is-closed' : ''}`} role="status">
            {waClosed ? (
              <>
                A janela de 24 horas do WhatsApp oficial encerrou. Só template aprovado chega ao contato.
                <button type="button" className="at-wa-window-action" onClick={() => setTemplatePicker(true)}>
                  Enviar template
                </button>
              </>
            ) : `Texto e arquivos livres por mais ${formatWindowLeft(dmExpiry, clock)}. Depois disso, só template aprovado.`}
          </div>
        )}
        {isDm && (
          <div className={`at-dm-window${dmClosed ? ' is-closed' : ''}`} role="status">
            {dmClosed
              ? `A janela de 24 horas do ${ch?.label || 'canal'} encerrou. Você só pode responder depois que o contato escrever de novo.`
              : `Responda em até ${formatWindowLeft(dmExpiry, clock)}. O ${ch?.label || 'canal'} aceita só texto por enquanto.`}
          </div>
        )}
        <textarea
          ref={textRef}
          rows={1}
          disabled={windowClosed}
          placeholder={windowClosed ? 'Janela de resposta encerrada' : 'Escreva uma mensagem...'}
          value={text}
          onSelect={event => { emojiSelection.current = { start: event.currentTarget.selectionStart, end: event.currentTarget.selectionEnd } }}
          onChange={(e) => {
            setText(e.target.value)
            autoGrow(e.target)
            notifyTypingRef.current?.()
          }}
          onKeyDown={onKeyDown}
        />

        <div className="at-composer-bar">
          <div className="at-composer-tools">
            <div className="at-menu-wrap at-emoji-wrap">
              <button
                className="at-tool-btn"
                title="Emoji"
                type="button"
                aria-label="Emojis"
                aria-expanded={pop === 'emoji'}
                aria-haspopup="dialog"
                aria-controls={pop === 'emoji' ? 'at-chat-emoji-picker' : undefined}
                onClick={() => {
                  emojiSelection.current = { start: textRef.current?.selectionStart ?? text.length, end: textRef.current?.selectionEnd ?? text.length }
                  setPop((p) => (p === 'emoji' ? null : 'emoji'))
                }}
              >
                <Icon n="smile" s={19} />
              </button>
              {pop === 'emoji' && (
                <>
                  <div className="at-menu-backdrop" onClick={closeEmojis} />
                  <Suspense fallback={<div className="at-emoji-panel at-emoji-loading" role="status">Carregando emojis…</div>}>
                    <ChatEmojiPicker onSelect={insertEmoji} onClose={closeEmojis} />
                  </Suspense>
                </>
              )}
            </div>

            <button
              className="at-tool-btn"
              title={isDm ? `O ${ch?.label || 'canal'} aceita só texto por enquanto` : 'Anexar arquivo'}
              onClick={() => fileRef.current?.click()}
              disabled={sending || isDm || waClosed}
            >
              <Icon n="clip" s={19} />
            </button>
            <input
              ref={fileRef}
              className="at-file-input"
              type="file"
              multiple
              accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rar"
              onChange={pickFiles}
            />

            <div className="at-menu-wrap">
              <button
                className="at-tool-btn"
                title="Respostas rápidas"
                onClick={() => setPop((p) => (p === 'quick' ? null : 'quick'))}
              >
                <Icon n="zap" s={19} />
              </button>
              {pop === 'quick' && (
                <>
                  <div className="at-menu-backdrop" onClick={() => setPop(null)} />
                  <div className="at-pop at-pop-quick">
                    <div className="at-menu-title">Respostas rápidas</div>
                    {QUICK_REPLIES.map((q) => (
                      <button key={q.id} onClick={() => insert(q.texto)}>{q.label}</button>
                    ))}
                  </div>
                </>
              )}
            </div>

          </div>

          <div className="at-composer-send">
            <button
              className="at-wa-btn"
              title={ch ? `Enviar por ${ch.label}` : 'Canal da conversa'}
              onClick={send}
              disabled={(!text.trim() && files.length === 0) || sending || windowClosed}
            >
              <Icon n="whatsapp" s={21} />
            </button>
            <button className="at-send-btn" aria-label="Enviar mensagem" onClick={send} disabled={(!text.trim() && files.length === 0) || sending || windowClosed}>
              <Icon n="send" s={17} />
            </button>
          </div>
        </div>
      </div>
      {templatePicker && (
        <WhatsappTemplatePicker
          accountId={conv.wa_instance_name}
          onClose={() => setTemplatePicker(false)}
          onSend={sendTemplate}
        />
      )}
    </section>
  )
}

export function MessageBubble({ msg, voiceStyle = false, avatar, senderName }) {
  const out = msg.sender_type === 'operador'
  const attachments = msg.attachments || []
  const inlineVoiceMeta = voiceStyle && attachments.at(-1)?.mime?.startsWith('audio/')
  const metadata = <span className="at-bubble-meta">
    {msgTime(msg.created_at)}
    {out && <Receipt status={msg.status} />}
  </span>
  if (msg.sender_type === 'sistema') {
    return <div className="at-sys-msg">{msg.body}</div>
  }
  return (
    <div className={`at-msg-row${out ? ' out' : ''}`}>
      <div className={`at-bubble${out ? ' out' : ''}${msg.status === 'falhou' ? ' failed' : ''}`}>
        {msg.body && <span className="at-bubble-body">{msg.body}</span>}
        {attachments.map((a, index) => (
          <AttachmentPreview key={a.id} attachment={a} voiceStyle={voiceStyle} outgoing={out} avatar={avatar} senderName={senderName} metadata={inlineVoiceMeta && index === attachments.length - 1 ? metadata : null} />
        ))}
        {!inlineVoiceMeta && metadata}
      </div>
    </div>
  )
}

function AttachmentPreview({ attachment, voiceStyle, outgoing, avatar, senderName, metadata }) {
  const { toast } = useApp()
  const [url, setUrl] = useState(null)
  const [err, setErr] = useState(null)
  const mime = attachment.mime || ''
  const isImage = mime.startsWith('image/')
  const isAudio = mime.startsWith('audio/')

  useEffect(() => {
    let alive = true
    if (attachment.local) return
    atDownloadAttachment(attachment.id)
      .then((res) => { if (alive) setUrl(res.url) })
      .catch((e) => { if (alive) setErr(e.message) })
    return () => { alive = false }
  }, [attachment.id, attachment.local])

  const open = async () => {
    try {
      const res = url ? { url } : await atDownloadAttachment(attachment.id)
      window.open(res.url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      toast?.(e.message || 'Falha ao abrir anexo.', 'error')
    }
  }

  if (isImage && url) {
    return (
      <button className="at-attach-img" onClick={open} title={attachment.nome}>
        <img src={url} alt={attachment.nome || 'Imagem'} loading="lazy" />
      </button>
    )
  }
  if (isAudio && voiceStyle) {
    return <VoiceMessage url={url} outgoing={outgoing} avatar={avatar} name={senderName} pending={attachment.local} error={err} onOpen={open} metadata={metadata}/>
  }
  if (isAudio && url) {
    return (
      <div className="at-attach-audio">
        <audio controls preload="metadata" src={url} />
        <button onClick={open} title="Abrir áudio">
          <Icon n="download" s={13} />
        </button>
      </div>
    )
  }
  return (
    <button className={`at-attach${err ? ' is-error' : ''}`} onClick={open} disabled={attachment.local}>
      <Icon n={err ? 'alert' : 'download'} s={13} />
      <span>{attachment.nome || 'Arquivo'}</span>
      {attachment.tamanho ? <small>{formatBytes(attachment.tamanho)}</small> : null}
    </button>
  )
}

function formatBytes(value) {
  const n = Number(value || 0)
  if (!n) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

// recibo do WhatsApp: relógio (enviando) · tick simples · tick duplo (azul se lido)
function Receipt({ status }) {
  if (status === 'enviando') return <span className="at-receipt"><Icon n="clock" s={12} /></span>
  if (status === 'falhou') return <span className="at-receipt"><Icon n="alert" s={12} /></span>
  if (status === 'enviado') return <span className="at-receipt"><Icon n="check" s={13} /></span>
  return (
    <span className={`at-receipt${status === 'lido' ? ' is-read' : ''}`}>
      <Icon n="ticks" s={14} />
    </span>
  )
}

function MessagesSkeleton() {
  return (
    <div className="at-skel-msgs">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className={`at-skel-bubble${i % 2 ? ' out' : ''}`} />
      ))}
    </div>
  )
}

/* ========================================================================= */
/* Coluna 3 — contexto do cliente                                            */
/* ========================================================================= */
const CONTEXT_READERS = {
  contact: getCrmContact, deals: getCrmContactDeals,
  activities: getCrmContactActivities, history: getCrmContactHistory,
}
const CONTEXT_TABS = [
  { id: 'history', label: 'Histórico' },
  { id: 'activities', label: 'Atividades' },
  { id: 'deals', label: 'Negócios' },
]
const ACT_STATUS = { pendente: 'Em aberto', atrasado: 'Atrasada', feito: 'Concluída' }
const DEAL_STATUS = { aberto: 'Em aberto', ganho: 'Ganho', perdido: 'Perdido' }

function ContactPane({ conv, onContactUpdated, onOpenHistoryConversation }) {
  const { toast } = useApp()
  const navigate = useNavigate()
  const nome = displayName(conv)
  const cid = conv.contact_id
  const [context, setContext] = useState({ contact: null, deals: [], activities: [], history: [], errors: {} })
  const { contact, deals, errors } = context
  const acts = context.activities.filter(activity => activity.tipo !== 'mensagem')
  const [loading, setLoading] = useState(!!cid)
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [tab, setTab] = useState('history')
  const [activityFilter, setActivityFilter] = useState('open')
  const [showAct, setShowAct] = useState(null)
  const [dealModal, setDealModal] = useState(null)
  const [busyActivity, setBusyActivity] = useState(null)
  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [nameSaving, setNameSaving] = useState(false)
  const [nameError, setNameError] = useState('')
  const nameLock = useRef(false)
  const [editingEmail, setEditingEmail] = useState(false)
  const [emailDraft, setEmailDraft] = useState('')
  const [emailSaving, setEmailSaving] = useState(false)
  const [emailError, setEmailError] = useState('')
  const emailLock = useRef(false)
  const generation = useRef(0)
  const activityLock = useRef(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const load = useCallback(async () => {
    const token = ++generation.current
    setLoading(!!cid)
    const next = await loadContactContext(cid, CONTEXT_READERS)
    if (!mounted.current || generation.current !== token) return
    setContext(next)
    setLoading(false)
  }, [cid])
  useEffect(() => { load() }, [load, conv.contact_nome])

  const toggleAct = async (act) => {
    if (activityLock.current) return
    activityLock.current = true
    setBusyActivity(act.id)
    try {
      await updateActivity(act.id, { status: act.status === 'feito' ? 'pendente' : 'feito' })
      if (mounted.current) await load()
    } catch (e) { if (mounted.current) toast?.(e.message, 'error') }
    finally {
      activityLock.current = false
      if (mounted.current) setBusyActivity(null)
    }
  }
  const onCreated = () => {
    if (!mounted.current) return
    setShowAct(null)
    load()
    toast?.('Atividade criada.', 'success')
  }
  const handleCreateDeal = () => {
    if (!cid) return
    setDealModal({ initialContact: { id: cid, nome: contact?.nome || nome } })
  }
  const saveName = async event => {
    event.preventDefault()
    if (!cid || nameLock.current) return
    const nextName = nameDraft.trim()
    if (!nextName) { setNameError('Informe o nome do contato.'); return }
    if (nextName === (contact?.nome || nome)) { setEditingName(false); return }
    nameLock.current = true
    setNameSaving(true); setNameError('')
    try {
      await updateCrmContact(cid, { nome: nextName })
      if (!mounted.current) return
      setContext(current => ({ ...current, contact: { ...current.contact, nome: nextName } }))
      onContactUpdated?.(cid, { contact_nome: nextName })
      setEditingName(false)
      load()
    } catch (error) { if (mounted.current) setNameError(error.message || 'Não foi possível salvar o nome.') }
    finally { nameLock.current = false; if (mounted.current) setNameSaving(false) }
  }

  const email = contact ? contact.email : conv.contact_email
  const saveEmail = async event => {
    event.preventDefault()
    if (!cid || emailLock.current) return
    const nextEmail = emailDraft.trim()
    if (nextEmail === (email || '')) { setEditingEmail(false); return }
    emailLock.current = true
    setEmailSaving(true); setEmailError('')
    try {
      await updateCrmContact(cid, { email: nextEmail })
      if (!mounted.current) return
      setContext(current => ({ ...current, contact: { ...current.contact, email: nextEmail } }))
      onContactUpdated?.(cid, { contact_email: nextEmail })
      setEditingEmail(false)
      load()
    } catch (error) { if (mounted.current) setEmailError(error.message || 'Não foi possível salvar o e-mail.') }
    finally { emailLock.current = false; if (mounted.current) setEmailSaving(false) }
  }
  const telefone = contact?.telefone || conv.contact_telefone
  const empresa = contact?.company_nome || contact?.empresa
  const contactCompany = contact?.company_id ? { id: contact.company_id, nome: empresa || 'Empresa vinculada' } : null
  const currentDealId = selectContextDeal(deals, selectedDeal, conv.deal_id)
  const pending = acts.filter(a => a.status !== 'feito').length
  const visibleActs = acts.filter(a => activityFilter === 'all' || (activityFilter === 'done' ? a.status === 'feito' : a.status !== 'feito'))
  const sectionError = errors[tab]

  return (
    <aside className="at-panel at-context" aria-label="Contexto do cliente">
      <div className="at-panel-head">
        <h3>Detalhes do contato</h3>
      </div>
      <>
        <div className="at-context-summary">
          <div className="at-panel-hero">
            <ContactAvatar conv={conv} nome={contact?.nome || nome} size="at-avatar-lg" />
            <div className="at-panel-hero-body">
              {editingName ? <form className="at-contact-name-editor" onSubmit={saveName} onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); if (!nameSaving) setEditingName(false) } }}>
                <input autoFocus aria-label="Nome do contato" maxLength={200} value={nameDraft} disabled={nameSaving} onChange={event => setNameDraft(event.target.value)} />
                <div><button type="submit" disabled={nameSaving}>{nameSaving ? 'Salvando…' : 'Salvar'}</button><button type="button" disabled={nameSaving} onClick={() => setEditingName(false)}>Cancelar</button></div>
                {nameError && <small role="alert">{nameError}</small>}
              </form> : <button type="button" className="at-panel-name at-contact-name" disabled={!cid} title="Editar nome do contato" onClick={() => { setNameDraft(contact?.nome || nome); setNameError(''); setEditingName(true) }}>{contact?.nome || nome}</button>}
              <span className="at-contact-phone"><Icon n="phone" s={12} />{telefone || 'Telefone não informado'}</span>
              <div className="pm-dp-drawer dp-embedded at-contact-company">
                <DealCompany compact company={contactCompany} unlinkedName={empresa || ''} disabled={!cid || loading || !!errors.contact}
                  onOpenDetails={() => { if (contactCompany?.id) navigate(`/comercial/empresas/${encodeURIComponent(contactCompany.id)}`) }}
                  onLinkCompany={company => updateCrmContact(cid, { company_id: company.id, empresa: company.nome })}
                  onSaved={company => { if (!mounted.current) return; setContext(current => ({ ...current, contact: { ...current.contact, company_id: company.id, company_nome: company.nome, empresa: company.nome } })); load() }} />
              </div>
              {contact?.cargo && <span className="at-panel-company">{contact.cargo}</span>}
              <div className="at-contact-info">
                <PanelField icon="mail" label="E-mail" value={editingEmail ? <form className="at-contact-name-editor at-contact-email-editor" onSubmit={saveEmail} onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); if (!emailSaving) setEditingEmail(false) } }}>
                  <input autoFocus type="email" aria-label="E-mail do contato" value={emailDraft} disabled={emailSaving} placeholder="Adicionar e-mail" onChange={event => setEmailDraft(event.target.value)} />
                  <div><button type="submit" disabled={emailSaving}>{emailSaving ? 'Salvando…' : 'Salvar'}</button><button type="button" disabled={emailSaving} onClick={() => setEditingEmail(false)}>Cancelar</button></div>
                  {emailError && <small role="alert">{emailError}</small>}
                </form> : <button type="button" className="at-contact-email" disabled={!cid || loading || !!errors.contact} title="Editar e-mail do contato" onClick={() => { setEmailDraft(email || ''); setEmailError(''); setEditingEmail(true) }}>{email || 'Adicionar e-mail'}</button>} />
                {contact?.owner_nome && <PanelField icon="user" label="Responsável" value={contact.owner_nome} />}
                {contact?.ad_origin && <AdOriginCard origin={contact.ad_origin} />}
                {Array.isArray(contact?.tags) && contact.tags.length > 0 && <div className="at-context-tags">{contact.tags.map((tag, index) => <span key={index}>{tag}</span>)}</div>}
                {errors.contact && <p className="at-context-error" role="alert">Não foi possível carregar os dados do contato. <button type="button" onClick={load}>Tentar novamente</button></p>}
              </div>
            </div>
          </div>
        </div>
        {(!currentDealId || deals.length > 1) && <div className="at-contact-business">
          {deals.length > 1 && <select aria-label="Negócio do contato" value={currentDealId || ''} onChange={event => setSelectedDeal(event.target.value)}>{deals.map(deal => <option key={deal.id} value={deal.id}>{deal.titulo || 'Negócio sem título'}</option>)}</select>}
          {!currentDealId && (loading ? <p className="at-muted" role="status">Carregando negócios…</p> : errors.deals ? <p className="at-context-error" role="alert">Não foi possível carregar os negócios. <button type="button" onClick={load}>Tentar novamente</button></p> : cid ? <div className="at-contact-no-deal"><p>Este contato ainda não tem negócio.</p><button type="button" className="at-contact-create" onClick={handleCreateDeal}><Icon n="plus" s={14} />Criar negócio</button></div> : <p className="at-muted">Conversa sem contato vinculado.</p>)}
        </div>}
        {currentDealId ? <DealPanel key={currentDealId} dealId={currentDealId} embedded compact onOpenHistoryConversation={onOpenHistoryConversation} onClose={() => setSelectedDeal(null)} onChanged={load} onDeleted={() => { setSelectedDeal(null); load() }} onCreateDeal={handleCreateDeal} /> : <>
        <div className="at-context-tabs" role="tablist" aria-label="Informações do cliente">
          {CONTEXT_TABS.map(item => <button key={item.id} id={`at-context-tab-${item.id}`} type="button" role="tab" aria-selected={tab === item.id} aria-controls="at-context-content" className={tab === item.id ? 'is-active' : ''} onClick={() => setTab(item.id)}>
            {item.label}{!loading && !errors[item.id] && item.id !== 'history' && <span>{item.id === 'activities' ? acts.length : deals.length}</span>}
          </button>)}
        </div>
        <div className="at-context-content" id="at-context-content" role="tabpanel" aria-labelledby={`at-context-tab-${tab}`} key={tab}>
          {!cid ? <ContextEmpty icon="user" title="Contato não vinculado" text="O histórico comercial aparece quando a conversa possui um contato vinculado." /> : tab === 'history' ? <ContactHistory key={cid} contactId={cid} onChanged={load} onOpenConversation={onOpenHistoryConversation} /> : <>
            <div className="at-context-toolbar">
              {tab === 'activities' && <select aria-label="Filtrar atividades" value={activityFilter} onChange={e => setActivityFilter(e.target.value)}><option value="open">Em aberto{!errors.activities && !loading ? ` (${pending})` : ''}</option><option value="done">Concluídas</option><option value="all">Todas</option></select>}
              {tab === 'deals' && <span>Negócios vinculados</span>}
              <button type="button" className="at-context-add" disabled={loading} onClick={tab === 'deals' ? handleCreateDeal : () => setShowAct('nova')}><Icon n="plus" s={13} />{tab === 'deals' ? 'Novo negócio' : 'Atividade'}</button>
            </div>
            {loading ? <p className="at-muted" role="status">Carregando contexto…</p> : sectionError ? <div className="at-context-error" role="alert">Não foi possível carregar esta seção. <button type="button" onClick={load}>Tentar novamente</button></div> : <>
              {tab === 'activities' && (visibleActs.length ? <div className="at-context-activities">{visibleActs.map(a => <article key={a.id} className={`at-act${a.status === 'feito' ? ' is-done' : ''}`}>
                <button type="button" className="at-act-check" disabled={busyActivity !== null} onClick={() => toggleAct(a)} aria-label={`${a.status === 'feito' ? 'Reabrir' : 'Concluir'} atividade ${a.titulo}`} title={a.status === 'feito' ? 'Reabrir atividade' : 'Concluir atividade'}>{busyActivity === a.id || a.status === 'feito' ? <Icon n={busyActivity === a.id ? 'refresh' : 'check'} s={17} /> : <span className="at-context-unchecked" />}</button>
                <div className="at-act-body"><span className="at-act-title">{a.titulo}</span><span className="at-act-meta"><Icon n={actIcon(a)} s={12} />{ACT_TIPO[a.tipo] || a.tipo || 'Tarefa'}<span className={a.status === 'atrasado' ? 'at-act-late' : ''}>{ACT_STATUS[a.status] || a.status}</span></span><span className="at-act-meta"><Icon n="calendar" s={12} />{prazoLabel(a.prazo) || 'Sem prazo'}{a.responsavel_nome && <span>· {a.responsavel_nome}</span>}</span></div>
              </article>)}</div> : <ContextEmpty icon="check" title="Nenhuma atividade neste filtro" text="Adicione uma atividade para acompanhar o próximo contato." />)}
              {tab === 'deals' && (deals.length ? <div className="at-context-deals">{deals.map(item => <button type="button" className="at-context-deal" key={item.id} onClick={() => setSelectedDeal(item.id)}>
                <span className="at-context-deal-top"><Icon n="sales" s={15} /><span className={`at-context-deal-status is-${item.status}`}>{DEAL_STATUS[item.status] || item.status}</span><Icon n="chevron_r" s={14} /></span>
                <strong>{item.titulo || 'Negócio sem título'}</strong><span>{[item.pipeline_nome, item.stage_nome].filter(Boolean).join(' · ') || 'Sem etapa informada'}</span>{money(item.valor) && <b>{money(item.valor)}</b>}
              </button>)}</div> : <ContextEmpty icon="sales" title="Nenhum negócio vinculado" text="Crie uma oportunidade para acompanhar este contato no Comercial." />)}
            </>}
          </>}
        </div>
        </>}
      </>
      {showAct && cid && <NewActivityModal contactId={cid} agendar={showAct === 'agendar'} onClose={() => setShowAct(null)} onCreated={onCreated} />}
      {dealModal && <DealModal dealId={dealModal.dealId || null} pipelineId={dealModal.pipelineId} stageId={dealModal.stageId} initialContact={dealModal.initialContact} onClose={() => setDealModal(null)} onSaved={() => { setDealModal(null); setSelectedDeal(null); load() }} />}
    </aside>
  )
}

function PanelField({ icon, label, value }) {
  return <div className="at-panel-field"><Icon n={icon} s={13} /><span className="at-panel-field-label">{label}</span><div className="at-panel-field-value">{value}</div></div>
}

function ContextEmpty({ icon, title, text }) {
  return <div className="at-context-empty"><Icon n={icon} s={22} /><strong>{title}</strong><p>{text}</p></div>
}

/* Modal "Nova atividade" / "Agendar" — cria uma atividade do CRM já vinculada ao
   contato da conversa (createCrmContactActivity). 'agendar' só muda o default do
   tipo (evento) e os rótulos; o backend é o mesmo. */
function NewActivityModal({ contactId, agendar, onClose, onCreated }) {
  const { toast } = useApp()
  const [titulo, setTitulo] = useState('')
  const [tipo, setTipo] = useState(agendar ? 'reuniao' : 'tarefa')
  const [prazo, setPrazo] = useState('')
  const [prioridade, setPrioridade] = useState('media')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const submit = async () => {
    const t = titulo.trim()
    if (!t || saving) return
    setSaving(true)
    try {
      const row = await createCrmContactActivity(contactId, {
        titulo: t,
        tipo,
        prioridade,
        prazo: prazo ? new Date(prazo).toISOString() : undefined,
      })
      onCreated(row)
    } catch (e) { toast?.(e.message, 'error'); setSaving(false) }
  }

  return (
    <div className="at-modal-overlay" onClick={onClose}>
      <div className="at-modal at-modal-form" onClick={(e) => e.stopPropagation()}>
        <div className="at-modal-head">
          <h3>{agendar ? 'Agendar' : 'Nova atividade'}</h3>
          <button className="at-icon-btn" onClick={onClose} aria-label="Fechar">
            <Icon n="close" s={16} />
          </button>
        </div>

        <div className="at-form">
          <label className="at-field">
            <span>Título</span>
            <input
              ref={inputRef}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: Retornar ligação"
              onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
            />
          </label>
          <div className="at-field-row">
            <label className="at-field">
              <span>Tipo</span>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                {Object.entries(ACT_TIPO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="at-field">
              <span>Prioridade</span>
              <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
                <option value="baixa">Baixa</option>
                <option value="media">Média</option>
                <option value="alta">Alta</option>
              </select>
            </label>
          </div>
          <label className="at-field">
            <span>{agendar ? 'Data e hora' : 'Prazo'}</span>
            <input type="datetime-local" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
          </label>
        </div>

        <div className="at-modal-foot">
          <button className="at-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="at-btn-primary" onClick={submit} disabled={!titulo.trim() || saving}>
            {saving ? 'Salvando…' : (agendar ? 'Agendar' : 'Criar')}
          </button>
        </div>
      </div>
    </div>
  )
}
